'use client'

import { memo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

/** Tiny 8-bit person: head + body + legs */
const PixelUser = memo(function PixelUser({
  className,
  accent = false,
}: {
  className?: string
  accent?: boolean
}) {
  const body = accent
    ? 'bg-[color:var(--ninxl-brand-teal)]'
    : 'bg-[color:var(--ninxl-theme-text)]'
  return (
    <div className={cn('flex flex-col items-center', className)} aria-hidden>
      <div className={cn('h-1.5 w-1.5 rounded-[1px]', body)} />
      <div className={cn('mt-px h-2.5 w-2 rounded-[1px]', body)} />
      <div className="mt-px flex gap-px">
        <div className={cn('h-1.5 w-1 rounded-[1px]', body)} />
        <div className={cn('h-1.5 w-1 rounded-[1px]', body)} />
      </div>
    </div>
  )
})

/** Small data packet block */
const DataPacket = memo(function DataPacket({
  label,
  className,
}: {
  label?: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-0.5 border border-[color:var(--ninxl-brand-teal)] bg-[color:var(--ninxl-theme-bg-frame)] px-1 py-0.5 shadow-[1px_1px_0_0_var(--ninxl-8bit-dim)]',
        className,
      )}
      aria-hidden
    >
      <span className="h-1.5 w-1.5 shrink-0 bg-[color:var(--ninxl-brand-teal)]" />
      {label ? (
        <span className="ninxl-8bit-label text-[6px] leading-none tracking-wider text-[color:var(--ninxl-theme-text-accent)] md:text-[7px]">
          {label}
        </span>
      ) : null}
    </div>
  )
})

/** Seated worker at a desk with laptop: typing glow on screen */
const DeskWorker = memo(function DeskWorker({
  className,
  accent = false,
  animate = true,
  delay = 0,
  faceRight = true,
}: {
  className?: string
  accent?: boolean
  animate?: boolean
  delay?: number
  faceRight?: boolean
}) {
  const body = accent
    ? 'bg-[color:var(--ninxl-brand-teal)]'
    : 'bg-[color:var(--ninxl-theme-text)]'

  return (
    <div
      className={cn('flex flex-col items-center', className)}
      style={{ transform: faceRight ? undefined : 'scaleX(-1)' }}
      aria-hidden
    >
      <div className="mb-px flex items-end gap-0.5">
        {/* Person (seated) */}
        <div className="flex flex-col items-center">
          <div className={cn('h-1.5 w-1.5 rounded-[1px]', body)} />
          <div className={cn('mt-px h-2 w-2.5 rounded-[1px]', body)} />
          <div className="mt-px flex gap-0.5">
            <div className={cn('h-1 w-1 rounded-[1px]', body)} />
            <div className={cn('h-1 w-1 rounded-[1px]', body)} />
          </div>
        </div>
        {/* Laptop */}
        <div className="relative flex flex-col items-center">
          <div className="relative h-3 w-4 border border-[color:var(--ninxl-theme-text)] bg-[color:var(--ninxl-theme-bg-frame)]">
            <div className="absolute inset-0.5 bg-[color:var(--ninxl-brand-teal)]/40" />
            {animate ? (
              <motion.span
                className="absolute bottom-0.5 left-0.5 h-0.5 bg-[color:var(--ninxl-brand-teal)]"
                animate={{ opacity: [1, 0.25, 1], width: [2, 6, 3, 8, 2] }}
                transition={{ duration: 1.4, delay, repeat: Infinity, ease: 'easeInOut' }}
              />
            ) : (
              <span className="absolute bottom-0.5 left-0.5 h-0.5 w-1.5 bg-[color:var(--ninxl-brand-teal)]" />
            )}
          </div>
          <div className="h-1 w-5 border border-[color:var(--ninxl-theme-text)]/60 bg-[color:var(--ninxl-theme-bg-frame)]" />
        </div>
      </div>
      {/* Desk surface + legs */}
      <div className="h-1 w-9 bg-[color:var(--ninxl-theme-text)]/70" />
      <div className="flex w-9 justify-between px-0.5">
        <div className="h-2 w-0.5 bg-[color:var(--ninxl-theme-text)]/50" />
        <div className="h-2 w-0.5 bg-[color:var(--ninxl-theme-text)]/50" />
      </div>
    </div>
  )
})

type DeskStation = {
  id: string
  left: string
  top: string
  accent?: boolean
  faceRight?: boolean
  delay: number
}

/** baby: Workers at desks inside the sanctuary (sides of the path) */
const DESK_STATIONS: DeskStation[] = [
  { id: 'desk-l1', left: '14%', top: '58%', accent: true, faceRight: true, delay: 0 },
  { id: 'desk-l2', left: '22%', top: '62%', faceRight: true, delay: 0.4 },
  { id: 'desk-r1', left: '78%', top: '58%', accent: true, faceRight: false, delay: 0.2 },
  { id: 'desk-r2', left: '70%', top: '62%', faceRight: false, delay: 0.7 },
]

type Walker = {
  id: string
  top: string
  duration: number
  delay: number
  from: string
  to: string
  accent?: boolean
}

const WALKERS: Walker[] = [
  { id: 'u1', top: '68%', duration: 9, delay: 0, from: '8%', to: '88%', accent: true },
  { id: 'u2', top: '72%', duration: 11, delay: 1.5, from: '90%', to: '10%' },
  { id: 'u3', top: '70%', duration: 10, delay: 3.2, from: '12%', to: '86%', accent: true },
  { id: 'u4', top: '74%', duration: 12, delay: 5, from: '85%', to: '14%' },
]

type OrbitPoint = { left: string; top: string }

type OrbitPacket = {
  id: string
  label: string
  /** Keyframe positions as percent of scene */
  path: OrbitPoint[]
  duration: number
  delay: number
}

/** baby: Data stays inside the temple: loops between pillars, never exits the arch */
const DATA_ORBITS: OrbitPacket[] = [
  {
    id: 'd1',
    label: 'CHAT',
    path: [
      { left: '18%', top: '42%' },
      { left: '48%', top: '36%' },
      { left: '78%', top: '42%' },
      { left: '48%', top: '52%' },
      { left: '18%', top: '42%' },
    ],
    duration: 8,
    delay: 0,
  },
  {
    id: 'd2',
    label: 'FILE',
    path: [
      { left: '78%', top: '48%' },
      { left: '52%', top: '40%' },
      { left: '22%', top: '48%' },
      { left: '52%', top: '56%' },
      { left: '78%', top: '48%' },
    ],
    duration: 9.5,
    delay: 1.2,
  },
  {
    id: 'd3',
    label: 'KEY',
    path: [
      { left: '50%', top: '34%' },
      { left: '72%', top: '44%' },
      { left: '50%', top: '54%' },
      { left: '28%', top: '44%' },
      { left: '50%', top: '34%' },
    ],
    duration: 7,
    delay: 2.4,
  },
  {
    id: 'd4',
    label: 'ID',
    path: [
      { left: '30%', top: '50%' },
      { left: '50%', top: '38%' },
      { left: '70%', top: '50%' },
      { left: '50%', top: '58%' },
      { left: '30%', top: '50%' },
    ],
    duration: 10,
    delay: 0.8,
  },
]

/**
 * Living sanctuary layer: walkers, desk workers with laptops, data packets
 * circulating inside (never leave the temple). Respects reduced-motion.
 */
export const NinOrgSceneLife = memo(function NinOrgSceneLife() {
  const reduceMotion = useReducedMotion()

  const desks = DESK_STATIONS.map((station) => (
    <div
      key={station.id}
      className="absolute z-[1]"
      style={{ left: station.left, top: station.top }}
    >
      <DeskWorker
        accent={station.accent}
        faceRight={station.faceRight}
        animate={!reduceMotion}
        delay={station.delay}
      />
    </div>
  ))

  if (reduceMotion) {
    return (
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {desks}
        <div className="absolute left-[20%] top-[42%]">
          <DataPacket label="DATA" />
        </div>
        <div className="absolute right-[20%] top-[46%]">
          <DataPacket label="SAFE" />
        </div>
        <div className="absolute left-[30%] top-[70%]">
          <PixelUser accent />
        </div>
        <div className="absolute right-[32%] top-[72%]">
          <PixelUser />
        </div>
      </div>
    )
  }

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {/* People at desks with laptops: working inside the sanctuary */}
      {desks}

      {/* Users walking through the sanctuary path */}
      {WALKERS.map((walker) => (
        <motion.div
          key={walker.id}
          className="absolute"
          style={{ top: walker.top }}
          initial={{ left: walker.from }}
          animate={{ left: [walker.from, walker.to, walker.from] }}
          transition={{
            duration: walker.duration,
            delay: walker.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <motion.div
            animate={{ y: [0, -2, 0, -2, 0] }}
            transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
          >
            <PixelUser accent={walker.accent} />
          </motion.div>
        </motion.div>
      ))}

      {/* Data packets orbiting inside: protected, never exit */}
      {DATA_ORBITS.map((packet) => {
        const leftKeyframes = packet.path.map((point) => point.left)
        const topKeyframes = packet.path.map((point) => point.top)
        const start = packet.path[0]
        return (
          <motion.div
            key={packet.id}
            className="absolute"
            initial={{ left: start?.left, top: start?.top }}
            animate={{ left: leftKeyframes, top: topKeyframes }}
            transition={{
              duration: packet.duration,
              delay: packet.delay,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <DataPacket label={packet.label} />
          </motion.div>
        )
      })}

      {/* Rising sealed sparks from the mandapa: data staying put */}
      {Array.from({ length: 6 }).map((_, i) => (
        <motion.span
          key={`spark-${i}`}
          className="absolute h-1 w-1 bg-[color:var(--ninxl-brand-teal)]"
          style={{ left: `${44 + i * 2.5}%`, bottom: '18%' }}
          animate={{
            y: [0, -28 - i * 4, -40 - i * 2],
            opacity: [0, 0.9, 0],
            scale: [0.6, 1, 0.4],
          }}
          transition={{
            duration: 2.2 + i * 0.15,
            delay: i * 0.35,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
      ))}

      {/* Soft shield pulse at arch center */}
      <motion.div
        className="absolute left-1/2 top-[40%] h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[color:var(--ninxl-brand-teal)]/30 md:h-20 md:w-20"
        animate={{ opacity: [0.15, 0.45, 0.15], scale: [0.92, 1.06, 0.92] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  )
})
